import { useState } from "react";
import { motion } from "framer-motion";
import { Bot, X, Send, Sparkles } from "lucide-react";

const services = [
  "Website Development",
  "Custom Software System",
  "Business Dashboard",
  "Booking System",
  "School Portal",
  "Healthcare System",
  "AI Chatbot Integration",
  "Secure Role-Based System",
];

function FloatingAIButton() {
  const [chatOpen, setChatOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const [messages, setMessages] = useState([
    {
      from: "bot",
      text: "Hi, I am ISSA AI. I can explain Eiffeltech services, suggest solutions, answer questions, or help you book a service step by step.",
    },
  ]);

  const sendBookingToWhatsApp = (data) => {
    const phone = "263716292923";

    const message = `Hello Eiffeltech, I want to book a service.

Service: ${data.service || "Not selected"}
Name: ${data.name || "Not provided"}
Contact: ${data.contact || "Not provided"}
Preferred Date: ${data.date || "Not provided"}
Details: ${data.details || "Not provided"}`;

    window.open(
      `https://wa.me/${phone}?text=${encodeURIComponent(message)}`,
      "_blank"
    );
  };

  const getFallbackReply = (text) => {
    const lower = text.toLowerCase();

    if (
      lower.includes("hi") ||
      lower.includes("hello") ||
      lower.includes("hey") ||
      lower.includes("hie")
    ) {
      return "Hello, welcome to Eiffeltech. What would you like us to help you build today?";
    }

    if (
      lower.includes("book") ||
      lower.includes("hire") ||
      lower.includes("consultation") ||
      lower.includes("appointment")
    ) {
      return `Great. I can help you prepare a booking request.

Please tell me:
1. Service you want
2. Your name
3. Phone or email
4. Preferred date
5. Project details

Or contact Eiffeltech directly on WhatsApp: +263716292923`;
    }

    if (
      lower.includes("service") ||
      lower.includes("offer") ||
      lower.includes("what do you do")
    ) {
      return `Eiffeltech provides:

${services.map((s, i) => `${i + 1}. ${s}`).join("\n")}

Which service are you interested in?`;
    }

    if (
      lower.includes("price") ||
      lower.includes("cost") ||
      lower.includes("how much") ||
      lower.includes("quote")
    ) {
      return "Pricing depends on the project size, features, design, and timeline. Tell me what you want to build and I can guide you.";
    }

    if (
      lower.includes("contact") ||
      lower.includes("phone") ||
      lower.includes("email") ||
      lower.includes("whatsapp")
    ) {
      return `You can contact Eiffeltech here:

Phone / WhatsApp: +263716292923
Email: eiffel1@eiffeltech.co.zw
Website: eiffeltech.co.zw`;
    }

    return "I can help with websites, custom systems, dashboards, portals, healthcare systems, AI chatbots, pricing, and bookings. What would you like to build?";
  };

  const getAIReply = async (userText) => {
    const history = messages.slice(-8).map((msg) => ({
      role: msg.from === "bot" ? "assistant" : "user",
      content: msg.text,
    }));

    const res = await fetch("/api/chat", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        message: userText,
        history,
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || "AI request failed");
    }

    return data.reply;
  };

  const askBot = async (customText = null) => {
    const userText = (customText || question).trim();

    if (!userText || isTyping) return;

    setMessages((prev) => [
      ...prev,
      { from: "user", text: userText },
    ]);

    setQuestion("");
    setIsTyping(true);

    try {
      const reply = await getAIReply(userText);

      setMessages((prev) => [
        ...prev,
        {
          from: "bot",
          text: reply,
        },
      ]);
    } catch (error) {
      const fallbackReply = getFallbackReply(userText);

      setMessages((prev) => [
        ...prev,
        {
          from: "bot",
          text: fallbackReply,
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const prepareWhatsAppBooking = () => {
    sendBookingToWhatsApp({
      service: "Eiffeltech Service",
      details: "I would like to discuss a project with Eiffeltech.",
    });
  };

  return (
    <>
      {/* FLOATING ROBOT BUTTON */}

     <motion.button
  onClick={() => setChatOpen(true)}
  className="fixed bottom-5 right-5 z-[999] flex h-20 w-20 items-center justify-center rounded-full text-white shadow-[0_0_35px_rgba(34,211,238,0.75)] sm:bottom-6 sm:right-6 sm:h-24 sm:w-24"
  animate={{
    y: [0, -10, 0],
    rotate: [0, 5, -5, 0],
    scale: [1, 1.08, 1],
    backgroundColor: [
      "rgb(168, 85, 247)",
      "rgb(34, 211, 238)",
      "rgb(59, 130, 246)",
      "rgb(236, 72, 153)",
      "rgb(168, 85, 247)",
    ],
    boxShadow: [
      "0 0 30px rgba(168,85,247,0.7)",
      "0 0 45px rgba(34,211,238,0.85)",
      "0 0 45px rgba(59,130,246,0.85)",
      "0 0 45px rgba(236,72,153,0.85)",
      "0 0 30px rgba(168,85,247,0.7)",
    ],
  }}
  transition={{
    duration: 3,
    repeat: Infinity,
    ease: "easeInOut",
  }}
>
  <Bot size={36} strokeWidth={3} />
</motion.button>
      {/* CHAT WINDOW */}

      {chatOpen && (
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-24 right-2 z-[999] w-[calc(100%-1rem)] max-w-[95vw] rounded-3xl border border-white/20 bg-black p-4 shadow-2xl  sm:right-6 sm:max-w-sm sm:p-5"
        >
          {/* HEADER */}

          <div className="mb-4 flex items-start justify-between">
            <div className="flex items-center gap-3">
              {/* ROBOT ICON */}

              <motion.div
                animate={{
                  boxShadow: [
                    "0 0 10px rgba(34,211,238,0.3)",
                    "0 0 25px rgba(168,85,247,0.8)",
                    "0 0 10px rgba(34,211,238,0.3)",
                  ],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                }}
                className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-violet-500"
              >
                <Bot size={28} className="text-white" strokeWidth={3} />
              </motion.div>

              <div>
                <h3 className="text-xl font-black ">
                  ISSA AI
                </h3>

                <p className="flex items-center gap-1 text-xs text-cyan-400 ">
                  <Sparkles size={12} />
                  Eiffeltech assistant
                </p>
              </div>
            </div>

            <button
              onClick={() => setChatOpen(false)}
              className="text-cyan-400 hover:text-red-500"
            >
              <X />
            </button>
          </div>

          {/* QUICK BUTTONS */}

          <div className="mb-3 flex flex-wrap gap-2">
            <button
              onClick={() =>
                askBot("What services does Eiffeltech offer?")
              }
              className="rounded-full bg-cyan-400/10 px-3 py-1.5 text-xs font-bold text-cyan-500 hover:bg-cyan-400 hover:text-slate-950"
            >
              Services
            </button>

            <button
              onClick={() =>
                askBot("How much does a website cost?")
              }
              className="rounded-full bg-cyan-400/10 px-3 py-1.5 text-xs font-bold text-cyan-500 hover:bg-cyan-400 hover:text-slate-950"
            >
              Pricing
            </button>

            <button
              onClick={() =>
                askBot("I want to book a service")
              }
              className="rounded-full bg-cyan-400/10 px-3 py-1.5 text-xs font-bold text-cyan-500 hover:bg-cyan-400 hover:text-slate-950"
            >
              Book
            </button>

            <button
              onClick={prepareWhatsAppBooking}
              className="rounded-full bg-cyan-400/10 px-3 py-1.5 text-xs font-bold text-cyan-500 hover:bg-cyan-400 hover:text-slate-950"
            >
              WhatsApp
            </button>
          </div>

          {/* CHAT BODY */}

          <div className="h-72 space-y-3 overflow-y-auto rounded-2xl bg-black p-3  sm:p-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`whitespace-pre-line rounded-2xl p-3 text-sm ${
                  msg.from === "bot"
                    ? "bg-black text-white  "
                    : "ml-auto bg-cyan-400 text-slate-950"
                }`}
              >
                {msg.text}
              </div>
            ))}

            {isTyping && (
              <motion.div
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                }}
                className="rounded-2xl bg-black p-3 text-sm text-white "
              >
                ISSA AI is analyzing...
              </motion.div>
            )}
          </div>

          {/* INPUT */}

          <div className="mt-4 flex gap-2">
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && askBot()
              }
              placeholder="Ask or book a service..."
              className="min-w-0 flex-1 rounded-full border px-4 py-3 text-sm text-white outline-none"
            />

            <button
              onClick={() => askBot()}
              className="rounded-full bg-cyan-400 px-4 text-slate-950 transition hover:scale-105"
            >
              <Send size={18} />
            </button>
          </div>
        </motion.div>
      )}
    </>
  );
}

export default FloatingAIButton;
